import type { StyleElements } from "replo-runtime/shared/utils/styles";
import type { CustomPropsRecord } from "schemas/component";
import type { ComponentConfig } from "../../components";

import { getDefaultStyleRulesIfEmpty } from "../../../shared/utils/breakpoints";
import { convertToLegacyProps } from "../../../shared/utils/props";

export function getConfigurableProps(): CustomPropsRecord {
  return {
    _urlHashmark: {
      type: "hashmark",
      name: "URL Hash Mark",
      defaultValue: null,
      description:
        "The URL will have this hash mark when this container is scrolled into view.",
    },
    _autoEnableHashmark: {
      type: "boolean",
      name: "Auto-enable URL Hash Mark",
      defaultValue: false,
      description:
        "If set, the page will automatically adopt this URL hashmark when the container scrolls into view.",
    },
    _urlHashmarkOffset: {
      type: "pixels",
      name: "URL Hash Mark Offset",
      defaultValue: "0px",
      description:
        "Offset (from top of page) to be added when scrolling to this hash mark.",
    },
    _accessibilityRole: {
      type: "string",
      name: "Accessibility Role",
      description:
        "Allows this container to be read as a different type of component by screen readers or other support interaction tools.",
      defaultValue: null,
    },
    _accessibilityHidden: {
      type: "boolean",
      name: "Accessibility Hidden",
      description:
        "If set, removes this component from the accessibility tree, hiding it from screen readers.",
      defaultValue: null,
    },
    _accessibilityChecked: {
      type: "boolean",
      name: "Accessibility Checked",
      description:
        "If set, the component will appear 'Checked' to screen readers. Useful for switches, checkboxes, and radio buttons.",
      defaultValue: null,
      shouldDisplay: ({ component }) => {
        const ariaRole = component.props._accessibilityRole;
        return (
          ariaRole === "switch" || component.props._accessibilityChecked != null
        );
      },
    },
    _accessibilityLive: {
      type: "selectable",
      name: "Accessibility Live",
      description:
        "If selected, sets the aria-live attribute on this container, which tells screen readers to announce dynamic content changes within this region.",
      selectableValues: {
        type: "options",
        options: [
          { value: "off", label: "Off" },
          { value: "polite", label: "Polite" },
          { value: "assertive", label: "Assertive" },
        ],
      },
      defaultValue: null,
      placeholder: "Not set",
    },
    _accessibilityLabelledBy: {
      type: "selectable",
      name: "Accessibility Labelled By",
      description:
        "If you select a Text component here, the Text component will be used to label this component in accessibility controls.",
      defaultValue: null,
      placeholder: "Select a component",
    },
    _accessibilityLabel: {
      type: "string",
      name: "Accessibility Label",
      description:
        "If provided, this will be used as the aria-label for this component.",
      defaultValue: undefined,
    },
    _accessibilityHtmlTagType: {
      type: "selectable",
      name: "Accessibility HTML Tag Type",
      description:
        "If selected, this container will use this HTML tag for display. Certain tag types (e.g. lists) are important to make your page accessible.",
      selectableValues: {
        type: "options",
        options: [
          { value: "div", label: "Div (<div>)" },
          { value: "ol", label: "Ordered List (<ol>)" },
          { value: "ul", label: "Unordered List (<ul>)" },
          { value: "li", label: "List Item (<li>)" },
          { value: "section", label: "Section (<section>)" },
          { value: "blockquote", label: "Block Quotation (<blockquote>)" },
          { value: "aside", label: "Aside (<aside>)" },
          { value: "label", label: "Label (<label>)" },
        ],
      },
      defaultValue: "div",
    },
  };
}

export const styleElements = {
  root: {
    defaultStyles: {
      display: "flex",
    },
    overrideStyles({ component, styleProps }) {
      return getDefaultStyleRulesIfEmpty(component, styleProps);
    },
  },
} satisfies StyleElements;

const config = {
  renderData: {
    customProps: convertToLegacyProps(getConfigurableProps()),
    newInstancesUseNumbering: true,
    allowsLayoutModification: true,
    acceptsArbitraryChildren: () => true,
    isAlwaysDynamic: false,
    canvasIndicatorDragDirections: [],
    canContainChildren: true,
    styleElements,
    showAccessibilityMenu: true,
  },
} satisfies ComponentConfig;

export default config;
